/**
 * Shared formatting for database ids (UUIDs) that need to appear in the UI.
 *
 * Raw UUIDs are unreadable and overflow their containers, so everywhere an id is
 * shown to a user it is reduced to its last few characters. The trailing characters
 * are used rather than the leading ones because they carry the most entropy, and the
 * "last 4" shape is already familiar to users from card and reference numbers.
 */

export const SHORT_ID_LENGTH = 4;

/**
 * The last few characters of an id, uppercased, without any prefix.
 *
 * Use this where a prefix would be wrong, such as inside a filename.
 * Returns an empty string when there is no id to format.
 */
export function shortIdRaw(id: string | null | undefined): string {
  if (!id) {
    return "";
  }

  const compact = id.replace(/-/g, "");
  if (compact.length === 0) {
    return "";
  }

  return compact.slice(-SHORT_ID_LENGTH).toUpperCase();
}

/**
 * The display form of an id: the last few characters, uppercased and prefixed
 * with "#" so it never reads as a quantity. Returns "" when there is no id.
 */
export function formatShortId(id: string | null | undefined): string {
  const short = shortIdRaw(id);
  if (!short) {
    return "";
  }

  return `#${short}`;
}

/**
 * What to show for a record that may carry a human-readable business reference
 * (a booking ref or invoice number) alongside its database id.
 *
 * The reference always wins, because it is meaningful to the user and is what
 * they will quote back to staff. The short id is only a fallback.
 */
export function formatRefDisplay(
  reference: string | null | undefined,
  id: string | null | undefined,
  fallback = "-",
): string {
  const trimmedReference = reference?.trim();
  if (trimmedReference) {
    return trimmedReference;
  }

  const short = formatShortId(id);
  if (short) {
    return short;
  }

  return fallback;
}

/**
 * Whether `formatRefDisplay` would fall back to the placeholder, meaning there is
 * nothing real to show and affordances such as copy or tooltip should be omitted.
 */
export function hasDisplayableId(
  reference: string | null | undefined,
  id: string | null | undefined,
): boolean {
  return Boolean(reference?.trim()) || Boolean(shortIdRaw(id));
}
